import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  User,
  ApiKey,
  ApiKeyCreateResponse,
  SupportTicket,
  SupportTicketDetail,
  SupportTicketCreateResponse,
  Channel,
  ChannelType,
  ChannelCreateResponse,
  Subscription,
  NotificationListResponse
} from '../types';
import { isMockAuth, getMockAuthSession } from '../test-utils/mock-auth';

const API_URL = import.meta.env.VITE_API_URL as string;

async function getAuthToken(): Promise<string> {
  if (isMockAuth()) {
    const session = getMockAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error('Not authenticated');
    return token;
  }
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  let token: string;
  try {
    token = await getAuthToken();
  } catch {
    window.location.href = '/signin';
    throw new Error('Session expired');
  }
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers
    }
  });
  if (response.status === 401) {
    window.location.href = '/signin';
    throw new Error('Session expired');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error((error as { error?: string }).error || 'Request failed');
  }
  const json = await response.json();
  // Backend handlers wrap successful payloads as {"data": {...}} via the
  // api-adapter's toApiGateway helper (while a handful — e.g. User_get —
  // return the shape flat). Auto-unwrap the envelope only when it's the
  // sole key, so flat responses pass through untouched.
  if (json && typeof json === 'object' && 'data' in json && Object.keys(json).length === 1) {
    return json.data as T;
  }
  return json as T;
}

export const api = {
  // User
  getUser: () => apiRequest<User>('/user/profile'),

  /**
   * Pre-signIn lookup — called before we fall back to signUp for an
   * identifier Cognito couldn't find. Returns whether any account
   * already exists under the same email/phone via a different auth
   * method (Google, GitHub) so we can steer the user to that button
   * and avoid duplicating the account. No auth required — inlines
   * its own fetch so it never redirects to /signin when there's no
   * session (which is the whole point).
   */
  identifierLookup: async (
    identifier: string,
  ): Promise<{ exists: boolean; methods: ('passwordless' | 'google' | 'github')[] }> => {
    const response = await fetch(`${API_URL}/auth/identifier-lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier }),
    });
    if (!response.ok) {
      // Anything other than 200 is a soft-fail: an attacker could spoof
      // a 500 to bypass the warning, but that just lands the user in
      // the old (pre-fix) path — nothing worse. Log and move on.
      return { exists: false, methods: [] };
    }
    return response.json();
  },

  /**
   * Paginated tweet-snapshot gallery. 403 for free / mid tiers —
   * the UI is expected to route non-eligible users to the teaser
   * page before calling this. Search filters on the X handle.
   */
  listSnapshots: (opts: { search?: string; nextToken?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.search) qs.set('search', opts.search);
    if (opts.nextToken) qs.set('nextToken', opts.nextToken);
    if (opts.limit) qs.set('limit', String(opts.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<{
      items: Array<{
        notificationId: string;
        tweetId: string;
        tweetText: string;
        tweetUrl: string;
        xUsername: string;
        snapshotUrl: string | null;
        createdAt: number | null;
      }>;
      nextToken?: string;
    }>(`/snapshots${suffix}`);
  },

  /**
   * Per-user soft-delete: clears SNAPSHOT_S3_KEY from every NOTIFICATIONS
   * row this user has for the given tweet. Idempotent — returns ok with
   * removed=0 if there was nothing to remove. Other users with the same
   * alert keep their snapshot tile.
   */
  deleteSnapshot: (tweetId: string) =>
    apiRequest<{ ok: boolean; removed: number }>(`/snapshots/${encodeURIComponent(tweetId)}`, {
      method: 'DELETE',
    }),

  /**
   * Hard-delete the authenticated account. `confirmation` must equal
   * "delete all" verbatim — enforced both in the modal and by the
   * backend Lambda so a CSRF/XHR without that body 400s. On success
   * the caller should sign out immediately; the JWT will stop
   * validating within a few seconds as Cognito propagates the delete.
   */
  deleteAccount: (confirmation: string) =>
    apiRequest<{ deleted: boolean; userId: string; summary: unknown }>('/user/me', {
      method: 'DELETE',
      body: JSON.stringify({ confirmation })
    }),

  // Billing
  createCheckout: (plan: string) =>
    apiRequest<{ url: string }>('/stripe/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan })
    }),

  createPortal: () =>
    apiRequest<{ url: string }>('/stripe/portal', { method: 'POST' }),

  // API keys
  listApiKeys: () => apiRequest<ApiKey[]>('/apikeys'),

  createApiKey: (name: string) =>
    apiRequest<ApiKeyCreateResponse>('/apikeys', {
      method: 'POST',
      body: JSON.stringify({ name })
    }),

  deleteApiKey: (key: string) =>
    apiRequest<{ success: boolean }>(`/apikeys/${encodeURIComponent(key)}`, {
      method: 'DELETE'
    }),

  // Support tickets
  createSupportTicket: (subject: string, message: string, category: string) =>
    apiRequest<SupportTicketCreateResponse>('/support', {
      method: 'POST',
      body: JSON.stringify({ subject, message, category })
    }),

  listSupportTickets: () => apiRequest<SupportTicket[]>('/support'),

  getSupportTicket: (ticketId: string) =>
    apiRequest<SupportTicketDetail>(`/support/${encodeURIComponent(ticketId)}`),

  replySupportTicket: (ticketId: string, message: string) =>
    apiRequest<{ success: boolean }>(`/support/${encodeURIComponent(ticketId)}/reply`, {
      method: 'POST',
      body: JSON.stringify({ message })
    }),

  // Channels (WhatsApp + email delivery destinations)
  createChannel: (input: { type: ChannelType; destination: string; displayName?: string; pendingXUsername?: string }) =>
    apiRequest<ChannelCreateResponse>('/channels', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  listChannels: () => apiRequest<{ channels: Channel[]; count: number }>('/channels'),

  verifyChannel: (channelId: string, code: string) =>
    apiRequest<{ channelId: string; status: string }>(`/channels/${encodeURIComponent(channelId)}/verify`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  resendChannelCode: (channelId: string) =>
    apiRequest<{ channelId: string; success: boolean }>(`/channels/${encodeURIComponent(channelId)}/resend`, {
      method: 'POST',
    }),

  updateChannel: (channelId: string, patch: Partial<Pick<Channel, 'displayName' | 'enabled' | 'isPrimary' | 'smsFallbackConsent'>>) =>
    apiRequest<Channel>(`/channels/${encodeURIComponent(channelId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteChannel: (channelId: string) =>
    apiRequest<{ success: boolean }>(`/channels/${encodeURIComponent(channelId)}`, {
      method: 'DELETE',
    }),

  // Subscriptions — no longer carry a target phone; alerts fan out to all enabled channels
  createSubscription: (xUsername: string) =>
    apiRequest<Subscription>('/subscriptions', {
      method: 'POST',
      body: JSON.stringify({ xUsername })
    }),

  listSubscriptions: async () => {
    // Handler returns { subscriptions, count } (inside the data envelope
    // that apiRequest unwraps). Callers treat the result as an array, so
    // peel the outer shape here.
    const r = await apiRequest<{ subscriptions: Subscription[]; count: number }>('/subscriptions');
    return r.subscriptions ?? [];
  },

  deleteSubscription: (xUsername: string) =>
    apiRequest<{ success: boolean }>(`/subscriptions/${encodeURIComponent(xUsername)}`, {
      method: 'DELETE'
    }),

  // Notifications
  listNotifications: (params?: { from?: number; to?: number; lastKey?: string }) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', String(params.from));
    if (params?.to) qs.set('to', String(params.to));
    if (params?.lastKey) qs.set('lastKey', params.lastKey);
    const q = qs.toString();
    return apiRequest<NotificationListResponse>(`/notifications${q ? `?${q}` : ''}`);
  }
};
